/**
 * Analog inputs, battery voltage and lift signal of units without our battery / lift modules (Teltonika
 * FMC130, ADR 0034). The unit sends raw millivolts of AIN1 / AIN2 (`ain1_mv`, `ain2_mv`) and its supply voltage
 * (`vin` = DC-DC converter output, NOT the battery). What a channel means is set per VEHICLE in the console
 * (vehicle_analog_calibration) and applied when reports are computed, so a corrected calibration also corrects
 * the history.
 *
 * Every rule here has a SQL twin in supabase/migrations/20270517000001_teltonika_analog.sql (same constants;
 * parity tests in apps/ingest/test/teltonika-analog.test.ts):
 *   analogValue            <-> fts_analog_value
 *   batteryVoltageOf       <-> fts_battery_v
 *   liftCycles             <-> fts_private.lift_cycles
 *   chargeSessionsFromVoltage <-> fts_private.charge_sessions (an ESTIMATE: no current is measured)
 *   lowBatteryStep         <-> fts_battery_low_report (at rest only, hysteresis, ADR 0027 timing)
 *
 * Measured data only: without a battery source there is no battery voltage (never the supply voltage unless
 * the calibration says the unit is fed straight from the traction battery). ADR 0003: everything here only
 * measures and counts; nothing can act on driving, lifting or the immobiliser.
 */

/** Where the battery voltage comes from. "module": our battery module (bat_v); "supply": the unit is fed
 * straight from the traction battery (vin, no DC-DC); "ain1"/"ain2": an analog input behind a voltage divider. */
export const BATTERY_SOURCES = ["none", "module", "supply", "ain1", "ain2"] as const;
export type BatterySource = (typeof BATTERY_SOURCES)[number];
/** Where the lift (mast) signal comes from: an analog input compared with an on / off threshold. */
export const LIFT_SOURCES = ["none", "ain1", "ain2"] as const;
export type LiftSource = (typeof LIFT_SOURCES)[number];

export type VehicleAnalogCalibration = {
  batterySource: BatterySource;
  /** Calibrated value = input volts × scale + offset. For a divider 1:4 the scale is 4. */
  batScale: number;
  batOffset: number;
  /** Low-battery alert below this voltage (V); null = no alert (owner decision: off until set). */
  batLowV: number | null;
  liftSource: LiftSource;
  liftScale: number;
  liftOffset: number;
  /** Lift active from this calibrated value up ... */
  liftOn: number | null;
  /** ... until it falls to this value or below (hysteresis: liftOff < liftOn). */
  liftOff: number | null;
  /** "Lift while driving": the lift is active while the speed is above this (km/h). Counted only. */
  liftDriveKmh: number;
  /** Charge detection fallback (no known charge level): rise (V) above the settled baseline, ignition off. */
  chargeRiseV: number;
  /** Charge level (V): null = 2.25 V/cell × cells of the vehicle's nominal voltage (24 V -> 27, 48 V -> 54, 80 V -> 90). */
  chargeV: number | null;
};

/** Defaults and bounds (same as the table's CHECK constraints). */
export const ANALOG_DEFAULTS = Object.freeze({
  batScale: 1,
  batOffset: 0,
  liftScale: 1,
  liftOffset: 0,
  liftDriveKmh: 3,
  chargeRiseV: 1.0,
  /** A charge session ends when the voltage fell this much (V) below its peak (charger disconnected). */
  chargeDropV: 1.0,
  /** Shorter sessions are not counted (voltage noise). */
  chargeMinS: 10 * 60,
  /** "Opportunity charging": a session shorter than this (s), e.g. a top-up during a break. */
  opportunityMaxS: 60 * 60,
  /** A data gap longer than this ends a lift cycle or a charge session (s). */
  maxGapS: 15 * 60,
  /** A lift cycle across a data gap longer than this is closed at its last active sample (s). */
  liftMaxGapS: 120,
});

export const ANALOG_LIMITS = Object.freeze({
  scale: { min: -1000, max: 1000 },
  offset: { min: -1000, max: 1000 },
  batLowV: { min: 5, max: 120 },
  liftThreshold: { min: -1000, max: 1000 },
  liftDriveKmh: { min: 0.5, max: 30 },
  chargeRiseV: { min: 0.2, max: 20 },
  chargeV: { min: 5, max: 120 },
  /** FMC130 analog inputs: 0-30 V. */
  ainMv: { min: 0, max: 30_000 },
});

export const defaultAnalogCalibration = (): VehicleAnalogCalibration => ({
  batterySource: "module",
  batScale: ANALOG_DEFAULTS.batScale,
  batOffset: ANALOG_DEFAULTS.batOffset,
  batLowV: null,
  liftSource: "none",
  liftScale: ANALOG_DEFAULTS.liftScale,
  liftOffset: ANALOG_DEFAULTS.liftOffset,
  liftOn: null,
  liftOff: null,
  liftDriveKmh: ANALOG_DEFAULTS.liftDriveKmh,
  chargeRiseV: ANALOG_DEFAULTS.chargeRiseV,
  chargeV: null,
});

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const inRange = (v: number, l: { min: number; max: number }) => v >= l.min && v <= l.max;

export type CalibrationIssue =
  | "same_channel"
  | "bat_scale"
  | "bat_offset"
  | "bat_low_v"
  | "lift_scale"
  | "lift_offset"
  | "lift_thresholds"
  | "lift_hysteresis"
  | "lift_drive_kmh"
  | "charge_rise_v"
  | "charge_v";

/** Validation (same rules as set_vehicle_analog_calibration). Empty = valid. */
export function calibrationIssues(c: VehicleAnalogCalibration): CalibrationIssue[] {
  const out: CalibrationIssue[] = [];
  if ((c.batterySource === "ain1" || c.batterySource === "ain2") && c.batterySource === c.liftSource) out.push("same_channel");
  if (!finite(c.batScale) || !inRange(c.batScale, ANALOG_LIMITS.scale) || c.batScale === 0) out.push("bat_scale");
  if (!finite(c.batOffset) || !inRange(c.batOffset, ANALOG_LIMITS.offset)) out.push("bat_offset");
  if (c.batLowV !== null && (!finite(c.batLowV) || !inRange(c.batLowV, ANALOG_LIMITS.batLowV))) out.push("bat_low_v");
  if (!finite(c.liftScale) || !inRange(c.liftScale, ANALOG_LIMITS.scale) || c.liftScale === 0) out.push("lift_scale");
  if (!finite(c.liftOffset) || !inRange(c.liftOffset, ANALOG_LIMITS.offset)) out.push("lift_offset");
  if (c.liftSource !== "none") {
    if (!finite(c.liftOn) || !finite(c.liftOff) || !inRange(c.liftOn, ANALOG_LIMITS.liftThreshold) || !inRange(c.liftOff, ANALOG_LIMITS.liftThreshold)) out.push("lift_thresholds");
    else if (c.liftOff >= c.liftOn) out.push("lift_hysteresis");
  }
  if (!finite(c.liftDriveKmh) || !inRange(c.liftDriveKmh, ANALOG_LIMITS.liftDriveKmh)) out.push("lift_drive_kmh");
  if (!finite(c.chargeRiseV) || !inRange(c.chargeRiseV, ANALOG_LIMITS.chargeRiseV)) out.push("charge_rise_v");
  if (c.chargeV !== null && (!finite(c.chargeV) || !inRange(c.chargeV, ANALOG_LIMITS.chargeV))) out.push("charge_v");
  return out;
}

/* ---------------- calibration math ---------------- */

/** Input millivolts -> calibrated value: (mv / 1000) × scale + offset, rounded to 0.01. Null without an input. */
export function analogValue(mv: number | null | undefined, scale: number, offset: number): number | null {
  if (!finite(mv) || !finite(scale) || !finite(offset)) return null;
  return Math.round(((mv / 1000) * scale + offset) * 100) / 100;
}

/** The measured values of one telemetry sample that can carry a battery voltage. */
export type AnalogSample = { batV?: number | null; vin?: number | null; ain1Mv?: number | null; ain2Mv?: number | null };

/**
 * Battery voltage of a sample by the vehicle's calibration; null when the source has no value or is "none".
 * "supply" applies scale and offset to the supply voltage in volts (the unit is fed by the battery itself).
 * A result outside 0..120 V (the ICD battery range) is a calibration or wiring fault and is left out.
 */
export function batteryVoltageOf(s: AnalogSample, c: Pick<VehicleAnalogCalibration, "batterySource" | "batScale" | "batOffset">): number | null {
  let v: number | null = null;
  switch (c.batterySource) {
    case "none":
      return null;
    case "module":
      v = finite(s.batV) ? s.batV : null;
      break;
    case "supply":
      v = finite(s.vin) ? Math.round((s.vin * c.batScale + c.batOffset) * 100) / 100 : null;
      break;
    case "ain1":
      v = analogValue(s.ain1Mv, c.batScale, c.batOffset);
      break;
    case "ain2":
      v = analogValue(s.ain2Mv, c.batScale, c.batOffset);
      break;
  }
  return v !== null && v >= 0 && v <= 120 ? v : null;
}

/** Calibrated lift signal of a sample; null when no lift source or no input value. */
export function liftValueOf(s: AnalogSample, c: Pick<VehicleAnalogCalibration, "liftSource" | "liftScale" | "liftOffset">): number | null {
  if (c.liftSource === "ain1") return analogValue(s.ain1Mv, c.liftScale, c.liftOffset);
  if (c.liftSource === "ain2") return analogValue(s.ain2Mv, c.liftScale, c.liftOffset);
  return null;
}

/* ---------------- lift cycles: analog threshold with hysteresis ---------------- */

export type LiftSample = { ts: number; value: number | null; speedKmh: number | null; ign?: boolean; op?: string | null };
export type LiftCycle = {
  start: number;
  end: number;
  durS: number;
  /** Seconds of the cycle with the speed above liftDriveKmh (sample intervals while active). */
  driveS: number;
  /** The lift was active while driving (counted, never blocked). */
  whileDriving: boolean;
  /** Card at the start of the cycle (the operator, resolved by the report). */
  op: string | null;
};

/**
 * Lift cycles from the calibrated signal (samples in device-time order). Active from value >= on until
 * value <= off (hysteresis). Only samples with the ignition on count: a cycle open when the ignition goes off
 * ends at the last ignition-on sample, and a mast still up after parking does not start a new cycle until it
 * came down (value <= off) once, so a raised mast while parked is at most one lift. The 300-s parked report
 * interval therefore never splits or creates lifts. A gap between ignition-on samples ends a cycle only when it
 * is longer than max(liftMaxGapS, 2 × the previous sample interval): a lift interrupted by a short reporting
 * gap stays one lift. A sample interval counts as "driving" when its starting sample is active and its speed
 * > driveKmh. A cycle still active at the last sample ends there.
 */
export function liftCycles(
  samples: readonly LiftSample[],
  t: { on: number; off: number; driveKmh: number; maxGapS?: number },
): LiftCycle[] {
  const minGap = (t.maxGapS ?? ANALOG_DEFAULTS.liftMaxGapS) * 1000;
  const out: LiftCycle[] = [];
  let cur: { start: number; driveMs: number; op: string | null } | null = null;
  let prev: LiftSample | null = null;
  let prevDt: number | null = null;
  /** The mast must come down once before a new cycle may start (after parking with it up). */
  let blocked = false;
  const close = (end: number) => {
    if (!cur) return;
    out.push({ start: cur.start, end, durS: Math.max(0, (end - cur.start) / 1000), driveS: Math.round(cur.driveMs / 1000), whileDriving: cur.driveMs > 0, op: cur.op });
    cur = null;
  };
  for (const s of samples) {
    if (s.ign === false) {
      // Parked: the open cycle ends at the last ignition-on sample; a mast still up must come down first.
      if (cur && prev) {
        close(prev.ts);
        blocked = true;
      }
      if (finite(s.value) && s.value <= t.off) blocked = false;
      // Intervals are measured between ignition-on samples of one run only.
      prev = null;
      prevDt = null;
      continue;
    }
    if (prev) {
      const dt = s.ts - prev.ts;
      if (cur && dt > Math.max(minGap, 2 * (prevDt ?? 0))) close(prev.ts);
      else if (cur && finite(prev.speedKmh) && prev.speedKmh > t.driveKmh) cur.driveMs += dt;
      prevDt = dt;
    }
    if (finite(s.value)) {
      if (s.value <= t.off) {
        blocked = false;
        if (cur) close(s.ts);
      } else if (!cur && !blocked && s.value >= t.on) {
        cur = { start: s.ts, driveMs: 0, op: s.op ?? null };
      }
    }
    prev = s;
  }
  if (cur && prev) close(prev.ts);
  return out;
}

/* ---------------- charge sessions (ESTIMATE from the voltage, ignition off) ---------------- */

export type VoltageSample = { ts: number; v: number | null; ign: boolean };
export type ChargeSessionV = {
  start: number;
  end: number;
  durS: number;
  /** Lowest smoothed voltage since the ignition went off (before the session). */
  startV: number;
  endV: number;
  peakV: number;
  /** Shorter than opportunityMaxS. */
  opportunity: boolean;
  /** Still charging at the last sample (the session may continue). */
  open: boolean;
};

/** Charge-level voltage per 2-V lead-acid cell: above it the battery is being charged (rest is ~2.10-2.15). */
export const CHARGE_V_PER_CELL = 2.25;
/** End level per cell (below it the charger stopped). */
export const CHARGE_END_V_PER_CELL = 2.2;
export const CHARGE_RULES = Object.freeze({
  /** Rolling median over this many readings (needs at least 3) suppresses single spikes. */
  medianN: 5,
  medianMin: 3,
  /** After ignition off, the baseline of the fallback rule is taken only after this settle time (rest recovery). */
  settleS: 10 * 60,
  /** The charge level must be held this long before a session starts. */
  holdS: 5 * 60,
});

/** The charge-level threshold: the calibration's value, else 2.25 V/cell × cells of the nominal voltage; null = unknown. */
export function chargeLevelV(chargeV: number | null | undefined, nominalV: number | null | undefined): number | null {
  if (finite(chargeV) && chargeV > 0) return chargeV;
  if (finite(nominalV) && nominalV > 0) return Math.round((nominalV / 2) * CHARGE_V_PER_CELL * 100) / 100;
  return null;
}

/** Median of the window; for an even count the lower middle value (no averaging: SQL percentile_disc(0.5)). */
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s[(s.length - 1) >> 1]!;
};

/**
 * Charge sessions estimated from the battery voltage (there is no current measurement). Only with the ignition
 * off; the voltage is smoothed with a rolling median of 5 readings (at least 3), so a single spike never counts.
 * Rule: the smoothed voltage reaches the charge level (`levelV`, default 2.25 V/cell) and stays there for
 * holdS (5 min); the session starts at the first reading at that level and ends at the last reading before the
 * smoothed voltage fell below the end level (2.20 V/cell), the ignition went on, or a data gap > maxGapS.
 * Lead-acid rest recovery (+1-2 V after unloading) stays far below the charge level. Without a known level
 * (no nominal voltage, no calibration value) the fallback is a rise of riseV above a baseline taken only after
 * the settle time (10 min) after ignition off, held for holdS. Sessions under minS are left out.
 */
export function chargeSessionsFromVoltage(
  samples: readonly VoltageSample[],
  p: { levelV?: number | null; riseV?: number; minS?: number; maxGapS?: number; opportunityMaxS?: number; settleS?: number; holdS?: number } = {},
): ChargeSessionV[] {
  const level = finite(p.levelV) ? p.levelV : null;
  const endLevel = level !== null ? (level * CHARGE_END_V_PER_CELL) / CHARGE_V_PER_CELL : null;
  const riseV = p.riseV ?? ANALOG_DEFAULTS.chargeRiseV;
  const minS = p.minS ?? ANALOG_DEFAULTS.chargeMinS;
  const maxGap = (p.maxGapS ?? ANALOG_DEFAULTS.maxGapS) * 1000;
  const oppS = p.opportunityMaxS ?? ANALOG_DEFAULTS.opportunityMaxS;
  const settle = (p.settleS ?? CHARGE_RULES.settleS) * 1000;
  const hold = (p.holdS ?? CHARGE_RULES.holdS) * 1000;
  const out: ChargeSessionV[] = [];
  let offAt: number | null = null;
  let buf: number[] = [];
  let low: number | null = null;
  let base: number | null = null;
  let cand: { ts: number; peak: number } | null = null;
  let cur: { start: number; startV: number; end: number; endV: number; peakV: number } | null = null;
  const close = (open: boolean) => {
    if (cur) {
      const durS = (cur.end - cur.start) / 1000;
      if (durS >= minS) out.push({ ...cur, durS, opportunity: durS < oppS, open });
    }
    cur = null;
    cand = null;
  };
  const reset = () => {
    offAt = null;
    buf = [];
    low = null;
    base = null;
  };
  let prevTs: number | null = null;
  for (const s of samples) {
    if (prevTs !== null && s.ts - prevTs > maxGap) {
      close(false);
      reset();
    }
    prevTs = s.ts;
    if (s.ign) {
      close(false);
      reset();
      continue;
    }
    if (offAt === null) offAt = s.ts;
    if (!finite(s.v)) continue;
    buf.push(s.v);
    if (buf.length > CHARGE_RULES.medianN) buf.shift();
    if (buf.length < CHARGE_RULES.medianMin) continue;
    const m = median(buf);
    const settled = s.ts - offAt >= settle;
    if (!cur) {
      if (low === null || m < low) low = m;
      if (settled && (base === null || m < base)) base = m;
      const charging = level !== null ? m >= level : base !== null && m - base >= riseV;
      if (!charging) {
        cand = null;
        continue;
      }
      if (!cand) cand = { ts: s.ts, peak: m };
      else cand.peak = Math.max(cand.peak, m);
      if (s.ts - cand.ts >= hold) cur = { start: cand.ts, startV: low, end: s.ts, endV: m, peakV: cand.peak };
    } else {
      const still = endLevel !== null ? m >= endLevel : base !== null && m - base >= riseV / 2;
      if (still) {
        cur.end = s.ts;
        cur.endV = m;
        if (m > cur.peakV) cur.peakV = m;
      } else {
        close(false);
        low = m;
        base = settled ? m : null;
      }
    }
  }
  close(true);
  return out;
}

/* ---------------- low-battery alert: at rest only, hysteresis, ADR 0027 timing ---------------- */

/** Same timing as the recorder health debounce (ADR 0027): raise after 10 min, clear after 2 min OK, 15 min gap restarts. */
export const LOW_BATTERY_DEBOUNCE = Object.freeze({
  raiseS: 10 * 60,
  clearS: 2 * 60,
  gapS: 15 * 60,
  /** Readings count only at rest: ignition off for this long (rest recovery done) ... */
  restOffS: 10 * 60,
  /** ... or ignition on, standing and the mast down for this long (light load). */
  restOnS: 5 * 60,
  /** Recovered only at threshold + this (V per 24 V nominal, at least 0.2 V): one reading at the edge changes nothing. */
  hysteresisPer24V: 0.2,
});

export type LowBatteryPeriod = { badSince: number; lastAt: number; okSince: number | null; alarmed: boolean };
export type LowBatteryState = { restSince: number | null; restIgn: boolean | null; period: LowBatteryPeriod | null };
export const initialLowBatteryState = (): LowBatteryState => ({ restSince: null, restIgn: null, period: null });
export type BatteryReading = { ts: number; v: number; ign: boolean; speedKmh: number | null; lifting: boolean };

/** Hysteresis above the threshold (V): 0.2 V per 24 V of the threshold, at least 0.2 V. */
export const lowBatteryHysteresisV = (lowV: number) => Math.max(0.2, Math.round(((lowV / 24) * LOW_BATTERY_DEBOUNCE.hysteresisPer24V) * 100) / 100);

/**
 * One battery reading (live, trusted time). Sag under load never counts: a reading is evaluated only at rest
 * (ignition off for restOffS, or standing with the mast down for restOnS). Low = v < lowV; recovered = v >= lowV
 * + hysteresis; a reading in between changes nothing (it neither starts nor resets the recovery). An alarm is
 * raised once per low period after raiseS; the period ends after clearS of recovered readings or a gap > gapS.
 */
export function lowBatteryStep(state: LowBatteryState, r: BatteryReading, lowV: number): { state: LowBatteryState; alarm: boolean } {
  const d = LOW_BATTERY_DEBOUNCE;
  const resting = !r.ign || ((r.speedKmh ?? 0) <= 0.5 && !r.lifting);
  const restSince = resting && state.restSince !== null && state.restIgn === r.ign ? state.restSince : resting ? r.ts : null;
  const next: LowBatteryState = { restSince, restIgn: resting ? r.ign : null, period: state.period };
  if (restSince === null || (r.ts - restSince) / 1000 < (r.ign ? d.restOnS : d.restOffS)) return { state: next, alarm: false };
  let p = state.period;
  if (p && (r.ts - p.lastAt) / 1000 > d.gapS) p = null;
  if (r.v < lowV) {
    p = p ? { ...p, lastAt: r.ts, okSince: null } : { badSince: r.ts, lastAt: r.ts, okSince: null, alarmed: false };
    if (!p.alarmed && (r.ts - p.badSince) / 1000 >= d.raiseS) return { state: { ...next, period: { ...p, alarmed: true } }, alarm: true };
    return { state: { ...next, period: p }, alarm: false };
  }
  if (!p) return { state: { ...next, period: null }, alarm: false };
  if (r.v >= lowV + lowBatteryHysteresisV(lowV)) {
    if (p.okSince !== null && (r.ts - p.okSince) / 1000 >= d.clearS) return { state: { ...next, period: null }, alarm: false };
    return { state: { ...next, period: { ...p, lastAt: r.ts, okSince: p.okSince ?? r.ts } }, alarm: false };
  }
  return { state: { ...next, period: { ...p, lastAt: r.ts } }, alarm: false };
}

/**
 * A suggested low-battery threshold for a nominal voltage (lead-acid rest voltage near 20 % charge, about
 * 1.93 V per 2-V cell). Only a suggestion shown in the console; the default stays "off" (owner decision).
 */
export function suggestedLowBatteryV(nominalV: number | null | undefined): number | null {
  if (!finite(nominalV) || nominalV <= 0) return null;
  return Math.round(nominalV * 0.965 * 10) / 10;
}

/* ---------------- immobiliser state (Teltonika IO 248) ---------------- */

/**
 * IO 248 (FMB family, to be verified on the FMC130): 0 no card presented, 1 card presented (immobiliser),
 * 2 card presented and authorised. The immobiliser ONLY prevents the first start without a card; it never
 * stops a running vehicle and has nothing to do with impacts (ADR 0003).
 */
export const IMMOBILIZER_STATES = ["no_card", "card", "authorized"] as const;
export type ImmobilizerState = (typeof IMMOBILIZER_STATES)[number];
export function immobilizerState(io248: number | undefined): ImmobilizerState | undefined {
  if (io248 === 0) return "no_card";
  if (io248 === 1) return "card";
  if (io248 === 2) return "authorized";
  return undefined;
}

/* ---------------- eco-driving events (Teltonika green driving IO 253 / 254) ---------------- */

export const ECO_EVENT_TYPES = ["harsh_accel", "harsh_brake", "harsh_corner"] as const;
export type EcoEventType = (typeof ECO_EVENT_TYPES)[number];
/** Plausible bound for the accelerometer value (g). */
export const ECO_MAX_G = 16;

/** IO 253 type (1 acceleration, 2 braking, 3 cornering) and IO 254 value (g × 100) -> event; undefined otherwise. */
export function ecoEventOf(type: number | undefined, value: number | undefined): { type: EcoEventType; g?: number } | undefined {
  const t = type === 1 ? "harsh_accel" : type === 2 ? "harsh_brake" : type === 3 ? "harsh_corner" : undefined;
  if (!t) return undefined;
  const g = finite(value) && value > 0 && value / 100 <= ECO_MAX_G ? Math.round(value) / 100 : undefined;
  return g === undefined ? { type: t } : { type: t, g };
}
